import { Component, Inject, ViewChild } from "@angular/core";
import { User } from "../../models/user";
import { UserProfile } from "../../models/user-profile";
import { UserProfileService } from "../../services/user-profile.service";
import { MD_DIALOG_DATA, MdDialog } from "@angular/material";
import { UserService } from "../../services/user.service";
import { CheckboxTableComponent } from "../../components/checkbox-table/checkbox-table.component";
import { Language } from "../../models/language";
import { LanguageService } from "../../services/language.service";
import { ConfirmComponent } from "../confirm/confirm.component";
import { UserActions } from "../../store/actions/user.actions";
import { UsersActions } from "../../store/actions/users.actions";
import { Store } from "@ngrx/store";
import { AdminService } from "../../services/admin.service";
import { NgForm } from "@angular/forms";
import { SuccessComponent } from "../success/success.component";

@Component({
  selector: "app-user-modal",
  templateUrl: "./user-modal.component.html",
  styleUrls: ["./user-modal.component.scss"],
})
export class UserModalComponent {
  user: User;
  loading: number;
  userProfiles: UserProfile[];
  selectedUserProfile: UserProfile;
  languages: Language[];
  allLanguage: Language;
  allLanguageToggle: boolean;
  currentUser: User;
  editMode: boolean;
  isPasswordInvalid: boolean;

  @ViewChild("checkboxTable") checkboxTable: CheckboxTableComponent;
  @ViewChild("userForm") userForm: NgForm;

  /**
   * Initialize variables and load data
   * @param {UserProfileService} userProfileService
   * @param {UserService} userService
   * @param {UsersActions} usersActions
   * @param {UserActions} userActions
   * @param {LanguageService} languageService
   * @param {MdDialog} mdDialog
   * @param {AdminService} adminService
   * @param {Store<any>} store
   * @param data
   */
  constructor(
    private userProfileService: UserProfileService,
    private userService: UserService,
    private usersActions: UsersActions,
    private userActions: UserActions,
    private languageService: LanguageService,
    private mdDialog: MdDialog,
    private adminService: AdminService,
    private store: Store<any>,
    @Inject(MD_DIALOG_DATA) public data: any
  ) {
    /**
     * Set the number of resources to fetch
     * @type {number}
     */
    this.loading = 3;
    this.isPasswordInvalid = false;
    this.allLanguage = new Language({
      id: -1,
      isDefault: false,
      value: "all",
    });
    this.allLanguageToggle = false;

    /**
     * Get user from modal config
     */
    this.getUser(data);

    /**
     * Fetch all user profiles from API
     */
    this.userProfileService.getAllUserProfiles().subscribe((profiles) => {
      this.userProfiles = profiles;
      this.loading--;
    });

    /**
     * Check the rigths for this component with the user privileges
     */
    this.adminService.init().subscribe(() => {
      this.editMode = this.adminService.can("Admin Panel", "Edit");
    });

    /**
     * Get the current user from the store
     */
    this.store
      .select("userState")
      .subscribe((user) => (this.currentUser = user));
  }

  /**
   * Fetch user by ID from the API and load the languages
   * @param data
   */
  getUser(data) {
    if (data.id) {
      this.userService.getUserById(data.id).subscribe((user) => {
        this.user = user;
        this.getLanguages();
        this.loading--;
      });
    } else {
      this.user = new User({
        languages: [],
      });
      this.getLanguages();
      this.loading--;
    }
  }

  /**
   * Fetch all languages from the API
   */
  getLanguages() {
    this.languageService.getAllLanguages().subscribe((languages) => {
      if (this.user.languages && this.user.languages.find((l) => l.isDefault)) {
        languages.find(
          (lang) => lang.id === this.user.languages.find((l) => l.isDefault).id
        ).isDefault = true;
      }
      this.languages = languages;
      this.loading--;
    });
  }

  /**
   * When a profile is selected, add it to the user profiles
   * @param event
   */
  profileSelected(event) {
    if (event.value) {
      this.user.profiles.push(event.value);
      setTimeout(() => {
        this.selectedUserProfile = null;
        event.source._element.nativeElement
          .getElementsByTagName("input")[0]
          .blur();
        this.checkboxTable.updatePrivileges();
      }, 20);
    }
  }

  /**
   * Remove profile from the user profiles
   * @param {UserProfile} userProfile
   */
  removeProfile(userProfile: UserProfile) {
    this.user.profiles = this.user.profiles.filter(
      (profile) => profile.id !== userProfile.id
    );
    setTimeout(() => this.checkboxTable.updatePrivileges(), 20);
  }

  /**
   * Update user privileges when the event is emitted
   */
  userPrivilegesUpdated() {
    this.user.userPrivileges = this.checkboxTable.settablePrivileges.filter(
      (privilege) => {
        return (
          privilege.checked === true && privilege.checkedByExtends === false
        );
      }
    );
  }

  /**
   * Update or create user with an API call
   */
  saveUserProfile() {
    this.user.userPrivileges = this.checkboxTable.settablePrivileges.filter(
      (privilege) => {
        return (
          privilege.checked === true && privilege.checkedByExtends === false
        );
      }
    );
    // Remove all language
    this.user.languages = this.user.languages.filter(
      (language) => language.id != -1
    );
    this.userService.saveUser(this.user).subscribe((users) => {
      if (typeof users === "string") {
        this.mdDialog.open(SuccessComponent, {
          data: { message: users, type: "warning" },
        });
      } else {
        this.usersActions.setUsers(users);
        this.userActions.loginUser(
          users.find((u) => u.id === this.currentUser.id)
        );
        this.mdDialog.closeAll();
        this.mdDialog.open(SuccessComponent, {
          data: { message: "User has been saved" },
        });
      }
    });
  }

  /**
   * Delete user with an API call
   */
  deleteUser() {
    const title = "Do you want to delete this user ?";
    const message = "Are you sure to delete this user ?";
    this.mdDialog
      .open(ConfirmComponent, { data: { title: title, message: message } })
      .afterClosed()
      .subscribe((res) => {
        if (res === true) {
          this.userService.deleteUser(this.user).subscribe((users) => {
            this.usersActions.setUsers(users);
            this.mdDialog.closeAll();
            this.mdDialog.open(SuccessComponent, {
              data: { message: "User has been deleted" },
            });
          });
        }
      });
  }

  /**
   * When user change languages selected, define the default language if none was defined
   */
  changeUserLanguages() {
    if (
      !this.allLanguageToggle &&
      this.user.languages.includes(this.allLanguage)
    ) {
      this.user.languages = Array.from(
        new Set(this.user.languages.concat(this.languages))
      );
      this.allLanguageToggle = true;
    } else if (
      this.allLanguageToggle &&
      !this.user.languages.includes(this.allLanguage)
    ) {
      this.allLanguageToggle = false;
      this.user.languages = [];
    }

    if (
      !this.user.defaultLanguage &&
      this.user.languages.length > 0 &&
      this.user.languages[0] != this.allLanguage
    ) {
      this.user.defaultLanguage = this.user.languages[0];
    }
  }

  resetPassword() {
    this.user.password = null;
    this.user.resetPassword = true;
  }

  checkPasswordPolicy() {
    this.isPasswordInvalid =
      !this.user.password ||
      this.user.password.length < 12 ||
      (this.user.login &&
        this.user.login.toLowerCase() === this.user.password.toLowerCase()) ||
      (this.user.email &&
        this.user.email.toLowerCase() === this.user.password.toLowerCase());
  }

  get editUser() {
    return this.user.id > 0;
  }
}



// WEBPACK FOOTER //
// ./src/app/modals/user-modal/user-modal.component.ts